import { useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { Phone, X } from 'lucide-react'
import { useFocusTrap } from '@/hooks/useFocusTrap'
import { useTenantStore } from '@/store/tenantStore'
import { Button } from '@/components/learner/ui/Button'
import {
  getDisplayablePhone,
  shouldShowTenantName,
} from './lib/recoveryTenantDisplay'

export interface AccountRecoveryModalProps {
  isOpen: boolean
  onClose: () => void
}

/**
 * LC 학습자 비로그인 화면에서 아이디/비밀번호 분실 시 학원 문의 안내 모달.
 *
 * - read-only: mutation / API 호출 없음 (self-service 미실행, spec §0 #1).
 * - 데이터 source: `tenantStore.tenantInfo` (phoneNumber + name).
 * - phoneNumber 없으면 fallbackContact 메시지 렌더 (spec §3.2 / §6).
 *
 * @see docs/superpowers/specs/2026-05-09-lc-learner-account-recovery-design.md
 */
export function AccountRecoveryModal({
  isOpen,
  onClose,
}: AccountRecoveryModalProps) {
  const { t } = useTranslation('learner')
  const { tenantInfo } = useTenantStore()
  const dialogRef = useRef<HTMLDivElement | null>(null)
  const closeButtonRef = useRef<HTMLButtonElement | null>(null)
  useFocusTrap(dialogRef, isOpen)

  // ESC 닫기
  useEffect(() => {
    if (!isOpen) return
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', handleKey)
    return () => window.removeEventListener('keydown', handleKey)
  }, [isOpen, onClose])

  // spec §7 "첫 포커스: 닫기 버튼"을 보장 — useFocusTrap이 X 버튼을 선택하지 않도록 명시적 focus.
  useEffect(() => {
    if (!isOpen) return
    // microtask 큐 한 번 비운 뒤 focus (useFocusTrap의 initial focus 다음에 덮어쓰기)
    const id = window.setTimeout(() => closeButtonRef.current?.focus(), 0)
    return () => window.clearTimeout(id)
  }, [isOpen])

  if (!isOpen) return null

  const phone = getDisplayablePhone(tenantInfo?.phoneNumber)
  const showName = shouldShowTenantName(tenantInfo?.name)

  // body로 portal — 부모(formPanel)의 transform(animate-lc-rise)이
  // position:fixed의 containing block이 되어 dim이 카드 안에 갇히는 문제 방지.
  return createPortal(
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="recovery-dialog-title"
      aria-describedby="recovery-dialog-description"
    >
      <div
        ref={dialogRef}
        className="relative flex w-full max-w-[400px] flex-col gap-6 rounded-[24px] bg-lc-bg-primary p-8 text-lc-text-primary shadow-[0_8px_12px_rgba(0,0,0,0.12)]"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label={t('auth.recovery.closeIconAriaLabel')}
          className="absolute right-4 top-4 flex size-8 items-center justify-center rounded-lg text-lc-text-secondary hover:bg-lc-v3-gray-100 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-lc-key-500"
        >
          <X className="size-5" aria-hidden="true" />
        </button>

        <div className="flex flex-col gap-3 pr-8">
          <h2
            id="recovery-dialog-title"
            className="text-pretty text-lc-v3-h4 font-bold text-lc-text-primary"
          >
            {t('auth.recovery.modalTitle')}
          </h2>
          <p
            id="recovery-dialog-description"
            className="text-lc-v3-body-1 text-lc-text-secondary"
          >
            {t('auth.recovery.body')}
          </p>
        </div>

        <div className="rounded-xl bg-lc-bg-key-light p-4">
          {phone ? (
            <a
              href={`tel:${phone}`}
              aria-label={t('auth.recovery.phoneAriaLabel', { phone })}
              className="inline-flex items-center gap-2 rounded-lg text-lc-v3-h5 font-semibold text-lc-text-link hover:text-lc-text-link-hover hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-lc-key-500"
            >
              <Phone className="size-5" aria-hidden="true" />
              {phone}
            </a>
          ) : (
            <p className="text-lc-v3-body-1 font-medium text-lc-text-primary">
              {t('auth.recovery.fallbackContact')}
            </p>
          )}

          {showName && tenantInfo?.name ? (
            <p className="mt-2 text-lc-v3-body-2 text-lc-text-secondary">
              {tenantInfo.name}
            </p>
          ) : null}
        </div>

        <p className="text-lc-v3-body-2 text-lc-text-secondary">
          {t('auth.recovery.help')}
        </p>

        <div className="flex justify-center">
          <Button
            ref={closeButtonRef}
            variant="neutral"
            size="M"
            onClick={onClose}
          >
            {t('auth.recovery.close')}
          </Button>
        </div>
      </div>
    </div>,
    document.body,
  )
}
